import React, { useState } from "react";
import { X, MapPin, Save, Navigation, Crosshair, Loader2 } from 'lucide-react';

export default function SucursalEditModal({ 
  sucursal, 
  sucursalIndex,
  clienteId,
  onSave, 
  onClose 
}) {
  const [formData, setFormData] = useState({
    nombre: sucursal?.nombre || sucursal || "",
    ubicacion_ciudad: sucursal?.ubicacion_ciudad || "",
    ubicacion_direccion: sucursal?.ubicacion_direccion || "",
    ubicacion_remota: sucursal?.ubicacion_remota || false,
    distancia_km: sucursal?.distancia_km || 0,
    coordenadas_lat: sucursal?.coordenadas_lat || "",
    coordenadas_lng: sucursal?.coordenadas_lng || "",
    orden_prioridad: sucursal?.orden_prioridad || 0,
    observaciones: sucursal?.observaciones || ""
  });

  const [autoUbicando, setAutoUbicando] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar que tenga al menos nombre
    if (!formData.nombre.trim()) {
      alert("El nombre de la sucursal es requerido");
      return;
    }

    // Convertir coordenadas a números si existen
    const sucursalData = {
      ...formData,
      coordenadas_lat: formData.coordenadas_lat ? parseFloat(formData.coordenadas_lat) : null,
      coordenadas_lng: formData.coordenadas_lng ? parseFloat(formData.coordenadas_lng) : null,
      distancia_km: parseFloat(formData.distancia_km) || 0,
      orden_prioridad: parseInt(formData.orden_prioridad) || 0
    };

    onSave(sucursalData, sucursalIndex);
  };

  const obtenerUbicacionGPS = () => {
    if (!navigator.geolocation) {
      alert("❌ Tu navegador no soporta geolocalización");
      return;
    }

    setAutoUbicando(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Obtener dirección desde coordenadas
          const respuesta = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'es',
                'User-Agent': 'Elements Client Management System'
              },
            }
          );

          if (respuesta.ok) {
            const data = await respuesta.json();
            const address = data?.address || {};
            
            const partes = [];
            if (address.road) partes.push(address.road);
            if (address.house_number) partes.push(address.house_number);
            if (address.neighbourhood) partes.push(address.neighbourhood);
            
            const direccionEspecifica = partes.length > 0 ? partes.join(', ') : data?.display_name || '';
            const ciudadPrincipal = address.city || address.town || address.village || address.county || '';

            setFormData({
              ...formData,
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
              ubicacion_direccion: direccionEspecifica,
              ubicacion_ciudad: ciudadPrincipal
            });
            
            alert(`✅ ¡Ubicación GPS completada!

📍 Coordenadas: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}
🏙️ Ciudad: ${ciudadPrincipal}
📌 Dirección: ${direccionEspecifica}`);
          } else {
            // Solo guardar coordenadas si falla la geocodificación
            setFormData({
              ...formData,
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6)
            });
            alert("✅ Coordenadas GPS capturadas.\nCompleta manualmente ciudad y dirección.");
          }
        } catch (error) {
          console.error("Error obteniendo dirección:", error);
          setFormData({
            ...formData,
            coordenadas_lat: latitude.toFixed(6),
            coordenadas_lng: longitude.toFixed(6)
          });
          alert("✅ Coordenadas GPS capturadas.\nCompleta manualmente ciudad y dirección.");
        } finally {
          setAutoUbicando(false);
        }
      },
      (error) => {
        console.error("Error de geolocalización:", error);
        let mensaje = "❌ No se pudo obtener tu ubicación.\n\n";
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            mensaje += "Permisos denegados. Ve a configuración del navegador y permite el acceso a ubicación.";
            break;
          case error.POSITION_UNAVAILABLE:
            mensaje += "Ubicación no disponible en este momento.";
            break;
          case error.TIMEOUT:
            mensaje += "La solicitud tardó demasiado tiempo.";
            break;
          default:
            mensaje += "Error desconocido.";
        }
        
        alert(mensaje);
        setAutoUbicando(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const completarDesdeGoogleMaps = async () => {
    let url = prompt('📍 Pega el enlace de Google Maps aquí:\n\nPuedes usar:\n• Enlaces completos: https://www.google.com/maps/...\n• Enlaces cortos: https://maps.app.goo.gl/...\n• O las coordenadas: -25.123, -57.456');
    
    if (!url || !url.trim()) return;
    
    url = url.trim();
    setAutoUbicando(true);
    
    try {
      let lat = null, lng = null;
      
      // Verificar si pegó coordenadas directamente
      const coordPattern = /^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/;
      const coordMatch = url.match(coordPattern);
      
      if (coordMatch) {
        lat = parseFloat(coordMatch[1]);
        lng = parseFloat(coordMatch[2]);
      }
      
      // Si no son coordenadas, intentar extraer de URL
      if (!lat || !lng) {
        // Si es enlace acortado, pedir que abra el enlace
        if (url.includes('goo.gl') || url.includes('maps.app.goo.gl')) {
          if (confirm(`🔗 Enlace acortado detectado\n\nPara obtener las coordenadas:\n\n1. Abre el enlace en una nueva pestaña\n2. Haz clic derecho en el lugar\n3. Copia las coordenadas (ej: -25.263, -57.606)\n4. Pégalas cuando te lo pida\n\n¿Abrir enlace ahora?`)) {
            window.open(url, '_blank');
            setAutoUbicando(false);
            setTimeout(() => completarDesdeGoogleMaps(), 1000);
            return;
          } else {
            setAutoUbicando(false);
            return;
          }
        }
        
        // Extraer de URL completa
        const patterns = [
          /@(-?\d+\.\d+),(-?\d+\.\d+)/,
          /place\/[^/]+\/@(-?\d+\.\d+),(-?\d+\.\d+)/,
          /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/,
          /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/,
          /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/
        ];
        
        for (const pattern of patterns) {
          const match = url.match(pattern);
          if (match) {
            lat = parseFloat(match[1]);
            lng = parseFloat(match[2]);
            break;
          }
        }
      }
      
      if (!lat || !lng) {
        alert(`❌ No se pudieron extraer coordenadas\n\n💡 Prueba:\n• Copiar las coordenadas directamente (-25.123, -57.456)\n• Usar URL completa de Google Maps\n• O usa el botón "Usar GPS"`);
        setAutoUbicando(false);
        return;
      }
      
      // Geocodificación inversa
      const respuesta = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'es',
            'User-Agent': 'Elements Client Management System'
          },
        }
      );

      if (!respuesta.ok) {
        throw new Error('Servicio de mapas no disponible');
      }

      const data = await respuesta.json();
      const address = data?.address || {};
      
      const partes = [];
      if (address.road) partes.push(address.road);
      if (address.house_number) partes.push(address.house_number);
      if (address.neighbourhood) partes.push(address.neighbourhood);
      
      const direccionEspecifica = partes.length > 0 ? partes.join(', ') : data?.display_name || '';
      const ciudadPrincipal = address.city || address.town || address.village || address.county || '';

      setFormData({
        ...formData,
        coordenadas_lat: lat.toFixed(6),
        coordenadas_lng: lng.toFixed(6),
        ubicacion_direccion: direccionEspecifica,
        ubicacion_ciudad: ciudadPrincipal
      });
      
      alert(`✅ ¡Ubicación completada desde Google Maps!

📍 Coordenadas: ${lat.toFixed(6)}, ${lng.toFixed(6)}
🏙️ Ciudad: ${ciudadPrincipal}
📌 Dirección: ${direccionEspecifica}`);
    } catch (error) {
      console.error('Error procesando ubicación:', error);
      alert('❌ Error al procesar la ubicación.\n\nIntenta copiar las coordenadas directamente.');
    } finally {
      setAutoUbicando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-emerald-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <MapPin className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {sucursalIndex >= 0 ? "Editar Sucursal" : "Nueva Sucursal"}
              </h3>
              <p className="text-sm text-gray-600">
                Configura la ubicación para agendamiento inteligente
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información Básica */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              📝 Información Básica
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Sucursal *
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: ASU - Asunción Central"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: Asunción"
                  value={formData.ubicacion_ciudad}
                  onChange={(e) => setFormData({ ...formData, ubicacion_ciudad: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Distancia desde sede (km)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="0"
                  value={formData.distancia_km}
                  onChange={(e) => setFormData({ ...formData, distancia_km: e.target.value })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Calle, número, barrio"
                  value={formData.ubicacion_direccion}
                  onChange={(e) => setFormData({ ...formData, ubicacion_direccion: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Coordenadas GPS */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-green-600" />
                Coordenadas GPS
              </h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={obtenerUbicacionGPS}
                  disabled={autoUbicando}
                  className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {autoUbicando ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Obteniendo...
                    </>
                  ) : (
                    <>
                      <Crosshair className="w-4 h-4" />
                      Usar GPS
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={completarDesdeGoogleMaps}
                  disabled={autoUbicando}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MapPin className="w-4 h-4" />
                  Google Maps
                </button>
              </div>
            </div>
            <p className="text-sm text-green-700 mb-4">
              🗺️ Las coordenadas GPS permiten agrupar sucursales cercanas en el mismo día
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Latitud
                </label>
                <input
                  type="number"
                  step="0.000001"
                  min="-90"
                  max="90"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="-25.2637"
                  value={formData.coordenadas_lat}
                  onChange={(e) => setFormData({ ...formData, coordenadas_lat: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Longitud
                </label>
                <input
                  type="number"
                  step="0.000001"
                  min="-180"
                  max="180"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="-57.5759"
                  value={formData.coordenadas_lng}
                  onChange={(e) => setFormData({ ...formData, coordenadas_lng: e.target.value })}
                />
              </div>
            </div>
            {formData.coordenadas_lat && formData.coordenadas_lng && (
              <div className="mt-3 p-2 bg-white rounded border border-green-200">
                <a
                  href={`https://www.google.com/maps?q=${formData.coordenadas_lat},${formData.coordenadas_lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-emerald-600 hover:underline flex items-center gap-1"
                >
                  <MapPin className="w-4 h-4" />
                  Ver en Google Maps →
                </a>
              </div>
            )}
          </div>

          {/* Configuración Avanzada */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              ⚙️ Configuración Avanzada
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    checked={formData.ubicacion_remota}
                    onChange={(e) => setFormData({ ...formData, ubicacion_remota: e.target.checked })}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Marcar como ubicación remota
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Las sucursales remotas no se agrupan automáticamente
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orden de prioridad
                </label>
                <input
                  type="number"
                  min="0"
                  max="999"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="0"
                  value={formData.orden_prioridad}
                  onChange={(e) => setFormData({ ...formData, orden_prioridad: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mayor número = mayor prioridad
                </p>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observaciones
              </label>
              <textarea
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Notas adicionales sobre esta sucursal..."
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
              />
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Guardar Sucursal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
